/**
 * Role-aware multi-chart dashboard panel + ALL CHARTS ARE CLICKABLE.
 *
 * Each chart element (bar, pie slice, line point) opens UniversalDrilldownModal
 * via the global DrilldownContext, showing the raw rows behind that data point.
 *
 * - Admin/Manager: 9 charts
 * - Sales TL: 2 charts (team)
 * - Sales: 2 charts (personal)
 * - RM: 1 chart (active accounts trend)
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from "recharts";
import { TrendingUp, BarChart3 } from "lucide-react";
import { API } from "../../lib/api";
import { fmtN } from "../../lib/format";
import { useDrilldown } from "./DrilldownContext";

const PALETTE = ["#1E5AA8", "#2E86DE", "#27AE60", "#F39C12", "#8E44AD", "#E74C3C", "#16A085", "#D35400"];

// Defensive: Recharts onClick payload may be either the row directly (v2) or {...,payload:row} (v3).
const get = (p, key) => (p && (p[key] !== undefined ? p[key] : p.payload?.[key]));

const fmtIDR = (v) =>
  v == null ? "—" :
  v >= 1_000_000_000 ? `${(v / 1_000_000_000).toFixed(1)}M` :
  v >= 1_000_000     ? `${(v / 1_000_000).toFixed(1)}Jt` :
  v >= 1_000          ? `${(v / 1_000).toFixed(0)}rb` :
  String(v);

const PIPELINE_STATUSES = ["New Lead","Cold Prospect","Prospective","Hot Prospect","New Account Sign","Existing Account","Churn Account","Lost Lead"];

function ChartShell({ title, icon: Icon, badge, children, testid, height = 280, hint = "Klik bar/segmen untuk drill-down" }) {
  return (
    <div className="card card-3d" data-testid={testid}>
      <div className="card-h" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {Icon && <Icon size={14} color="var(--accent)" />}
        <strong>{title}</strong>
        {badge && <span className="badge" style={{ marginLeft: "auto" }}>{badge}</span>}
      </div>
      <div className="card-b" style={{ height }}>
        {children}
      </div>
      <div style={{ fontSize: 10, color: "var(--muted)", padding: "4px 14px 8px 14px", borderTop: "1px dashed var(--border-2)" }}>
        💡 {hint}
      </div>
    </div>
  );
}

// ─────────── Admin / Manager: 9-chart panel ───────────
export function AdminChartsPanel() {
  const [d, setD] = useState(null);
  const { openDrilldown } = useDrilldown();
  useEffect(() => {
    axios.get(`${API}/dashboard/charts`).then((r) => setD(r.data?.admin)).catch(() => setD(null));
  }, []);
  if (!d) return <div className="empty">Memuat charts...</div>;

  return (
    <div data-testid="admin-charts-panel">
      <div className="section-title" style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 18 }}>
        <BarChart3 size={14} color="var(--accent)" /> Enterprise Analytics · Multi-Chart View · semua chart bisa di-klik
      </div>

      <div className="grid row-split" style={{ marginTop: 10 }}>
        {/* 1. Expected Target vs Deals Won */}
        <ChartShell title="Expected Target vs Deals Won" badge="Students" testid="ch-target-vs-won">
          <ResponsiveContainer width="100%" height="100%" minHeight={200}>
            <BarChart data={d.target_vs_won}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="name" /><YAxis />
              <Tooltip formatter={(v) => fmtN(v)} />
              <Bar dataKey="students" radius={[6, 6, 0, 0]} cursor="pointer"
                onClick={(p) => openDrilldown({
                  title: get(p, "name") === "Target" ? "All Leads (Target Pool)" : "Deals Won (New + Existing)",
                  metric: get(p, "name") === "Target" ? "leads_total" : "leads_won",
                })}>
                {d.target_vs_won.map((_, i) => <Cell key={i} fill={i === 0 ? "#94a3b8" : "#10b981"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartShell>

        {/* Sales Pipeline race dihapus dari Dashboard sesuai permintaan user */}
      </div>

      <div className="grid row-split" style={{ marginTop: 14 }}>
        {/* 3. Leads % per Phase (over months) */}
        <ChartShell title="Leads Percentage per Phase (2024+)" badge="By Month" testid="ch-leads-phase" height={300}>
          <ResponsiveContainer width="100%" height="100%" minHeight={200}>
            <BarChart data={d.leads_per_phase}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month" fontSize={11} /><YAxis />
              <Tooltip /><Legend />
              {PIPELINE_STATUSES.filter((s) =>
                d.leads_per_phase.some((row) => (row[s] || 0) > 0)
              ).map((s, i) => (
                <Bar key={s} dataKey={s} stackId="phase" fill={PALETTE[i % PALETTE.length]} cursor="pointer"
                  onClick={(p) => openDrilldown({
                    title: `${s} · ${get(p, "month")}`,
                    metric: "leads_by_phase_month", value: s, month: get(p, "month"),
                  })} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartShell>

        {/* 4. Deals Won vs Lost */}
        <ChartShell title="Deals Won vs Lost" badge="% Students" testid="ch-winloss" height={300}>
          <ResponsiveContainer width="100%" height="100%" minHeight={200}>
            <PieChart>
              <Pie data={d.win_loss} dataKey="value" nameKey="name" outerRadius={90}
                label={({ name, pct }) => `${name} ${pct}%`} cursor="pointer"
                onClick={(p) => openDrilldown({
                  title: get(p, "name") === "Won" ? "Deals Won" : "Deals Lost",
                  metric: get(p, "name") === "Won" ? "leads_won" : "leads_lost",
                })}>
                {d.win_loss.map((entry, i) => (
                  <Cell key={i} fill={entry.name === "Won" ? "#10b981" : "#ef4444"} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => fmtN(v)} />
            </PieChart>
          </ResponsiveContainer>
        </ChartShell>
      </div>

      <div className="grid row-split" style={{ marginTop: 14 }}>
        {/* 5. Students Trends */}
        <ChartShell title="Students Trends" badge="Signed Students/Mo" testid="ch-students-trend">
          <ResponsiveContainer width="100%" height="100%" minHeight={200}>
            <BarChart data={d.students_trends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month" fontSize={11} /><YAxis />
              <Tooltip formatter={(v) => fmtN(v)} />
              <Bar dataKey="students" fill="#27AE60" radius={[6, 6, 0, 0]} cursor="pointer"
                onClick={(p) => openDrilldown({
                  title: `Signed Students · ${get(p, "month")}`,
                  metric: "leads_signed_by_month", month: get(p, "month"),
                })} />
            </BarChart>
          </ResponsiveContainer>
        </ChartShell>

        {/* 6. Churn Trends */}
        <ChartShell title="Churn Trends" badge="Lost Students/Mo" testid="ch-churn-trend">
          <ResponsiveContainer width="100%" height="100%" minHeight={200}>
            <BarChart data={d.churn_trends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month" fontSize={11} /><YAxis />
              <Tooltip formatter={(v) => fmtN(v)} />
              <Bar dataKey="students" fill="#E74C3C" radius={[6, 6, 0, 0]} cursor="pointer"
                onClick={(p) => openDrilldown({
                  title: `Churn · ${get(p, "month")}`,
                  metric: "leads_churn_by_month", month: get(p, "month"),
                })} />
            </BarChart>
          </ResponsiveContainer>
        </ChartShell>
      </div>

      <div className="grid row-split" style={{ marginTop: 14 }}>
        {/* 7. Top products */}
        <ChartShell title="Product Terlaku" badge="Most Signed" testid="ch-top-products" height={280}>
          {d.top_products.length === 0 ? <div className="empty">Belum ada data.</div> :
            <ResponsiveContainer width="100%" height="100%" minHeight={200}>
              <PieChart>
                <Pie data={d.top_products} dataKey="value" nameKey="name" outerRadius={85}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} cursor="pointer"
                  onClick={(p) => openDrilldown({
                    title: `Top Product · ${get(p, "name")}`,
                    metric: "leads_by_package_signed", value: get(p, "name"),
                  })}>
                  {d.top_products.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          }
        </ChartShell>

        {/* 8. Hardest products — berdasarkan konversi terendah */}
        <ChartShell title="Product Tersulit Dijual" badge="Konversi Terendah" testid="ch-hardest-products" height={280}
          hint="Paling sering ditawarkan tapi % konversi ke New Assign/Active paling rendah">
          {d.hardest_products.length === 0 ? <div className="empty">Belum ada data.</div> :
            <ResponsiveContainer width="100%" height="100%" minHeight={200}>
              <BarChart data={d.hardest_products} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                <YAxis type="category" dataKey="name" width={160} fontSize={11} />
                <Tooltip formatter={(v, _n, p) => [`${v}% konversi (${p?.payload?.converted}/${p?.payload?.offered})`, "Konversi"]} />
                <Bar dataKey="conversion_pct" radius={[0, 6, 6, 0]} cursor="pointer"
                  onClick={(p) => openDrilldown({
                    title: `Hardest Product · ${get(p, "name")}`,
                    metric: "leads_by_package_all", value: get(p, "name"),
                  })}>
                  {d.hardest_products.map((_, i) => <Cell key={i} fill={["#E74C3C", "#F39C12", "#8E44AD", "#2E86DE", "#27AE60"][i % 5]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          }
        </ChartShell>
      </div>

      {/* 9. Avg Pricing per product */}
      <div style={{ marginTop: 14 }}>
        <ChartShell title="Average Pricing per Product" badge="Tuition Fee" testid="ch-avg-pricing" height={300}>
          {d.avg_pricing.length === 0 ? <div className="empty">Belum ada data.</div> :
            <ResponsiveContainer width="100%" height="100%" minHeight={200}>
              <BarChart data={d.avg_pricing} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis type="number" tickFormatter={fmtIDR} />
                <YAxis type="category" dataKey="package" width={180} fontSize={11} />
                <Tooltip formatter={(v) => `Rp ${fmtN(v)}`} />
                <Bar dataKey="avg_price" fill="#1E5AA8" radius={[0, 6, 6, 0]} cursor="pointer"
                  onClick={(p) => openDrilldown({
                    title: `Pricing detail · ${get(p, "package")}`,
                    metric: "leads_by_package_all", value: get(p, "package"),
                  })} />
              </BarChart>
            </ResponsiveContainer>
          }
        </ChartShell>
      </div>
    </div>
  );
}

// ─────────── Sales TL: team charts ───────────
export function SalesTLChartsPanel() {
  const [d, setD] = useState(null);
  const { openDrilldown } = useDrilldown();
  useEffect(() => {
    axios.get(`${API}/dashboard/charts`).then((r) => setD(r.data?.sales_tl)).catch(() => setD(null));
  }, []);
  if (!d) return null;
  return (
    <div data-testid="stl-charts-panel">
      <div className="section-title" style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 18 }}>
        <BarChart3 size={14} color="var(--accent)" /> Team <strong>{d.my_team || "—"}</strong> · Performance Charts
      </div>
      <div className="grid row-split" style={{ marginTop: 10 }}>
        <ChartShell title="Per Team · Expected Target vs Deals Won" badge="Students" testid="stl-ch-target">
          <ResponsiveContainer width="100%" height="100%" minHeight={200}>
            <BarChart data={d.team_target_vs_won}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="name" /><YAxis />
              <Tooltip formatter={(v) => fmtN(v)} />
              <Bar dataKey="students" radius={[6, 6, 0, 0]} cursor="pointer"
                onClick={(p) => openDrilldown({
                  title: get(p, "name") === "Target" ? "Team Leads (All)" : `Team ${d.my_team} · Deals Won`,
                  metric: get(p, "name") === "Target" ? "leads_total" : "leads_won",
                })}>
                {d.team_target_vs_won.map((_, i) => <Cell key={i} fill={i === 0 ? "#94a3b8" : "#10b981"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartShell>
        <ChartShell title="Team Leads Performance Monitor" badge="Race" testid="stl-ch-team-perf">
          <ResponsiveContainer width="100%" height="100%" minHeight={200}>
            <BarChart data={d.team_performance} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="name" width={120} fontSize={11} />
              <Tooltip />
              <Legend />
              <Bar dataKey="leads" stackId="a" fill="#2E86DE" name="Total Leads" cursor="pointer"
                onClick={(p) => openDrilldown({ title: `${get(p, "name")} · All Leads`, metric: "leads_assigned_to", username: get(p, "username") })} />
              <Bar dataKey="signed" stackId="b" fill="#10b981" name="Signed" cursor="pointer"
                onClick={(p) => openDrilldown({ title: `${get(p, "name")} · Signed`, metric: "leads_assigned_to_signed", username: get(p, "username") })} />
              <Bar dataKey="hot" stackId="c" fill="#F39C12" name="Hot Prospect" cursor="pointer"
                onClick={(p) => openDrilldown({ title: `${get(p, "name")} · Hot Prospect`, metric: "leads_assigned_to_hot", username: get(p, "username") })} />
            </BarChart>
          </ResponsiveContainer>
        </ChartShell>
      </div>
    </div>
  );
}

// ─────────── Sales: individual perf charts ───────────
export function SalesChartsPanel() {
  const [d, setD] = useState(null);
  const { openDrilldown } = useDrilldown();
  useEffect(() => {
    axios.get(`${API}/dashboard/charts`).then((r) => setD(r.data?.sales)).catch(() => setD(null));
  }, []);
  if (!d) return null;
  return (
    <div data-testid="sp-charts-panel">
      <div className="section-title" style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 18 }}>
        <BarChart3 size={14} color="var(--accent)" /> My Performance Charts
      </div>
      <div className="grid row-split" style={{ marginTop: 10 }}>
        <ChartShell title="My Activity Trend" badge="Activities/Mo" testid="sp-ch-activity">
          <ResponsiveContainer width="100%" height="100%" minHeight={200}>
            <LineChart data={d.my_activity_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month" fontSize={11} /><YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="activities" stroke="#2E86DE" strokeWidth={3}
                dot={{ r: 5, cursor: "pointer" }}
                activeDot={{ r: 7, cursor: "pointer",
                  onClick: (_, p) => openDrilldown({
                    title: `My Activities · ${p.payload.month}`,
                    metric: "my_activity_month", month: p.payload.month,
                  })
                }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartShell>
        <ChartShell title="My Pipeline" badge="By Status" testid="sp-ch-pipeline">
          <ResponsiveContainer width="100%" height="100%" minHeight={200}>
            <BarChart data={d.my_pipeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="status" fontSize={10} angle={-12} textAnchor="end" height={70} interval={0} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#1E5AA8" radius={[6, 6, 0, 0]} cursor="pointer"
                onClick={(p) => openDrilldown({
                  title: `My Leads · ${get(p, "status")}`,
                  metric: "my_pipeline_status", value: get(p, "status"),
                })} />
            </BarChart>
          </ResponsiveContainer>
        </ChartShell>
      </div>
    </div>
  );
}

// ─────────── RM: active accounts trend ───────────
export function RMChartsPanel() {
  const [d, setD] = useState(null);
  const { openDrilldown } = useDrilldown();
  useEffect(() => {
    axios.get(`${API}/dashboard/charts`).then((r) => setD(r.data?.rm)).catch(() => setD(null));
  }, []);
  if (!d) return null;
  return (
    <div data-testid="rm-charts-panel">
      <div className="section-title" style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 18 }}>
        <TrendingUp size={14} color="var(--accent)" /> Active Accounts Trend
      </div>
      <ChartShell title="Active Accounts · Students & Value Over Time" badge="By Signing Month" testid="rm-ch-trend" height={300}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={d.rm_trend}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="month" fontSize={11} />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" tickFormatter={fmtIDR} />
            <Tooltip formatter={(v, n) => n === "value" ? `Rp ${fmtN(v)}` : fmtN(v)} />
            <Legend />
            <Bar yAxisId="left" dataKey="students" fill="#27AE60" name="Students" radius={[6, 6, 0, 0]} cursor="pointer"
              onClick={(p) => openDrilldown({
                title: `Active Accounts Signed · ${get(p, "month")}`,
                metric: "leads_signed_by_month", month: get(p, "month"),
              })} />
            <Bar yAxisId="right" dataKey="value" fill="#1E5AA8" name="Est. Value" radius={[6, 6, 0, 0]} cursor="pointer"
              onClick={(p) => openDrilldown({
                title: `Active Accounts Signed (value) · ${get(p, "month")}`,
                metric: "leads_signed_by_month", month: get(p, "month"),
              })} />
          </BarChart>
        </ResponsiveContainer>
      </ChartShell>
    </div>
  );
}
